import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

type NotifMap = Record<string, string[]>;
type LabelMap = Record<string, string>;

type ChatNotificationsContextType = {
  addNotification: (tenantId: string, conversationId: string, companyName: string) => void;
  clearConversation: (tenantId: string, conversationId: string) => void;
  totalCount: number;
  countPerTenant: Record<string, number>;
  labelPerTenant: LabelMap;
};

const ChatNotificationsContext = createContext<ChatNotificationsContextType | undefined>(undefined);

export function ChatNotificationsProvider({ children }: { children: ReactNode }) {
  const [notifs, setNotifs] = useState<NotifMap>({});
  const [labels, setLabels] = useState<LabelMap>({});

  const addNotification = useCallback((tenantId: string, conversationId: string, companyName: string) => {
    setNotifs((prev) => {
      const existing = prev[tenantId] || [];
      if (existing.includes(conversationId)) return prev;
      return { ...prev, [tenantId]: [...existing, conversationId] };
    });
    setLabels((prev) => companyName ? { ...prev, [tenantId]: companyName } : prev);
  }, []);

  const clearConversation = useCallback((tenantId: string, conversationId: string) => {
    setNotifs((prev) => {
      const existing = prev[tenantId];
      if (!existing) return prev;
      const next = existing.filter((id) => id !== conversationId);
      if (next.length === existing.length) return prev;
      return { ...prev, [tenantId]: next };
    });
  }, []);

  const countPerTenant: Record<string, number> = {};
  let totalCount = 0;
  for (const [tenantId, ids] of Object.entries(notifs)) {
    countPerTenant[tenantId] = ids.length;
    totalCount += ids.length;
  }

  return (
    <ChatNotificationsContext.Provider value={{ addNotification, clearConversation, totalCount, countPerTenant, labelPerTenant: labels }}>
      {children}
    </ChatNotificationsContext.Provider>
  );
}

export function useChatNotifications() {
  const ctx = useContext(ChatNotificationsContext);
  if (!ctx) throw new Error('useChatNotifications must be used within ChatNotificationsProvider');
  return ctx;
}
